"use client";

import React, { useCallback } from "react";
import { VStack } from "@chakra-ui/react";
import usePageSearch from "../hooks/usePageSearch";
import { DEFAULT_HITS_PER_PAGE } from "../config/meiliSearchConfig";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import {
  SearchHitsPerPageSelector,
  SearchSemanticRatioSlider,
  SearchLanguageRefinements,
  SearchClearLanguageFiltersButton,
} from "./MeiliSearchControls";

function NavbarSearchControls() {
  const {
    searchControls,
    relevantSearchConfig,
    hasLanguageFilters,
    hasActiveQuery,
  } = usePageSearch();

  const handleLanguageToggle = useCallback(
    (language: string) => {
      const currentLanguages = searchControls.filters.language || [];
      const newLanguages = currentLanguages.includes(language)
        ? currentLanguages.filter((l) => l !== language)
        : [...currentLanguages, language];
      searchControls.setFilters({ language: newLanguages });
    },
    [searchControls],
  );

  const handleClearFilters = useCallback(() => {
    searchControls.setFilters({ language: [] });
  }, [searchControls]);

  return (
    <VStack align="stretch">
      <SearchSemanticRatioSlider
        stateObject={[searchControls.semanticRatio]}
        stateObjectSetter={(vals: number[]) =>
          vals.length && searchControls.setSemanticRatio(vals[0])
        }
        title="Semantic-to-keyword search ratio"
        isDecimal={true}
      />

      <SearchHitsPerPageSelector
        value={searchControls.hitsPerPage}
        onChange={(v: number) => searchControls.setHitsPerPage(v)}
        items={DEFAULT_HITS_PER_PAGE.map((i) => ({
          value: parseInt(i.value, 10),
          label: i.label,
        }))}
      />

      {!hasActiveQuery && (
        <GenericSelector
          previewItems={relevantSearchConfig.sortBy}
          stateObject={[searchControls.sortBy]}
          stateObjectSetter={(vals: string[]) =>
            vals.length && searchControls.setSortBy(vals[0])
          }
          labelText="Sort by (when not in search)"
          mode="select"
          placeholder="Sort by..."
          disableClear={true}
        />
      )}

      <SearchLanguageRefinements
        selectedLanguages={searchControls.filters.language || []}
        onLanguageToggle={handleLanguageToggle}
        title="Filter by Language"
      />

      <SearchClearLanguageFiltersButton
        onClear={handleClearFilters}
        hasFilters={hasLanguageFilters}
      />
    </VStack>
  );
}

export default React.memo(NavbarSearchControls);
